// ======================================================
// FILE: src/components/ProfilePage.js
// Business-only profile page
// ======================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useAccess } from "../access/AccessContext";
import { getStorageLimitBytes } from "../lib/planLimits";
import { clearSessionBypass, loadMfaProfile, saveMfaProfile } from "../utils/mfa";

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = Math.max(0, Number(bytes || 0));
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  const digits = n >= 10 || i < 2 ? 0 : 1;
  return `${n.toFixed(digits)} ${units[i]}`;
}

function useOptionalAccess() {
  try {
    const ctx = useAccess();
    return ctx || { readOnly: false };
  } catch {
    return { readOnly: false };
  }
}

export default function ProfilePage({ user, onUpdate, onLogout }) {
  const { readOnly } = useOptionalAccess();
  const navigate = useNavigate();

  const uid = user?.uid || "";
  const isManagerReadOnlyView = !!(readOnly || user?.managerOnly === true || user?.role === "account_manager");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [usage, setUsage] = useState({
    storageBytes: 0,
    objectCount: 0,
    updatedAt: null,
  });
  const [loadingUsage, setLoadingUsage] = useState(false);

  const [resetBusy, setResetBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetErr, setResetErr] = useState("");

  const [showDel, setShowDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState("");

  const [mfaState, setMfaState] = useState({ loading: true, enabled: false });
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaErr, setMfaErr] = useState("");
  const [mfaMsg, setMfaMsg] = useState("");

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || user.displayName || "",
      email: user.email || user.username || "",
      phone: user.phone || user.phoneNumber || "",
      address: user.address || "",
    });
  }, [user]);

  const limitBytes = useMemo(() => getStorageLimitBytes(user), [user]);
  const pct = limitBytes > 0 ? Math.min(100, Math.round((usage.storageBytes / limitBytes) * 100)) : 0;
  const storageAtLimit = limitBytes > 0 && usage.storageBytes >= limitBytes;

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const fetchUsage = useCallback(async () => {
    const cu = auth.currentUser;
    if (!cu) return;

    setLoadingUsage(true);
    try {
      const tok = await cu.getIdToken();
      const response = await fetch("/api/usage/getMy", {
        headers: { Authorization: `Bearer ${tok}` },
      });

      if (response.ok) {
        const j = await response.json();
        setUsage({
          storageBytes: Number(j.storageBytes || 0),
          objectCount: Number(j.objectCount || 0),
          updatedAt: j.updatedAt || null,
        });
      } else {
        setUsage({ storageBytes: 0, objectCount: 0, updatedAt: null });
      }
    } catch {
      setUsage({ storageBytes: 0, objectCount: 0, updatedAt: null });
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  const fetchMfa = useCallback(async () => {
    if (!uid) {
      setMfaState({ loading: false, enabled: false });
      return;
    }

    setMfaErr("");
    setMfaMsg("");
    setMfaState((prev) => ({ ...prev, loading: true }));

    try {
      const profile = await loadMfaProfile(uid);
      setMfaState({ loading: false, enabled: !!profile?.enabled });
    } catch (e) {
      setMfaState({ loading: false, enabled: false });
      setMfaErr(e?.message || "Unable to load MFA status.");
    }
  }, [uid]);

  useEffect(() => {
    if (uid) fetchUsage();
  }, [uid, fetchUsage]);

  useEffect(() => {
    fetchMfa();
  }, [fetchMfa]);

  async function handleSave(e) {
    e.preventDefault();
    if (!uid || readOnly) return;

    setSaving(true);
    setMsg("");
    setErr("");

    try {
      const payload = {
        name: form.name.trim(),
        displayName: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "users", uid), payload, { merge: true });

      onUpdate &&
        onUpdate({
          ...user,
          name: payload.name,
          displayName: payload.displayName,
          phone: payload.phone,
          address: payload.address,
        });

      setMsg("Profile updated successfully.");
    } catch (e2) {
      setErr(e2?.message || "Could not save your profile.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSendResetLink() {
    setResetBusy(true);
    setResetMsg("");
    setResetErr("");

    try {
      const cu = auth.currentUser;
      if (!cu) throw new Error("Please sign in again.");
      const tok = await cu.getIdToken(true);

      const res = await fetch("/api/auth/sendResetSelf", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${tok}`,
        },
      });

      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || "Failed to send reset email");
      setResetMsg("Password reset email sent.");
    } catch (e) {
      setResetErr(e?.message || "Unable to send reset email.");
    } finally {
      setResetBusy(false);
    }
  }

  async function handleMfaToggle() {
    if (mfaState.loading || mfaBusy || isManagerReadOnlyView) return;

    setMfaBusy(true);
    setMfaErr("");
    setMfaMsg("");

    try {
      if (mfaState.enabled) {
        const existing = (await loadMfaProfile(uid)) || {};
        await saveMfaProfile(uid, {
          ...existing,
          enabled: false,
          updatedAtMs: Date.now(),
        });
        clearSessionBypass(uid);
        setMfaState({ loading: false, enabled: false });
        setMfaMsg("Two-factor authentication disabled.");
      } else {
        navigate("/security");
        return;
      }
    } catch (e) {
      setMfaErr(e?.message || "Unable to update MFA settings.");
    } finally {
      setMfaBusy(false);
    }
  }

  async function handleDeleteAccount() {
    setDelErr("");
    setDelBusy(true);

    try {
      const cu = auth.currentUser;
      if (!cu) throw new Error("Please sign in again.");

      const tok = await cu.getIdToken();
      const resp = await fetch("/api/deleteAccount", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tok}`,
        },
        body: JSON.stringify({}),
      });

      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(j?.error || "Could not delete account");
      }

      try {
        await signOut(auth);
      } catch {}

      navigate("/login", { replace: true });
    } catch (e) {
      setDelErr(e?.message || "Could not delete account");
      setDelBusy(false);
    }
  }

  async function handleLogoutClick() {
    if (typeof onLogout === "function") {
      onLogout();
      return;
    }

    try {
      await signOut(auth);
    } catch {}
    navigate("/login", { replace: true });
  }

  return (
    <div className="pf-wrap">
      <header className="pf-header">
        <div className="pf-title">Account</div>
        <div className="pf-statuses">
          <span className={"pf-badge " + (user?.emailVerified ? "pf-badge--ok" : "")}>
            {user?.emailVerified ? "Verified email" : "Unverified email"}
          </span>
          {user?.isAdmin ? <span className="pf-badge pf-badge--admin">Admin</span> : null}
          {user?.role ? <span className="pf-badge">Role: {user.role}</span> : null}
        </div>
      </header>

      {msg ? <div className="pf-alert pf-alert--ok">{msg}</div> : null}
      {err ? <div className="pf-alert pf-alert--err">{err}</div> : null}

      <form className="pf-grid" onSubmit={handleSave}>
        <label className="pf-row">
          <span className="pf-label">Full name</span>
          <input
            className="pf-input"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="Your full name"
            autoComplete="name"
          />
        </label>

        <label className="pf-row">
          <span className="pf-label">Email</span>
          <input
            className="pf-input"
            type="email"
            value={form.email}
            readOnly
            placeholder="name@example.com"
            autoComplete="email"
          />
        </label>

        <label className="pf-row">
          <span className="pf-label">Phone</span>
          <input
            className="pf-input"
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="+353 01 234 5678"
            autoComplete="tel"
          />
        </label>

        <label className="pf-row" style={{ gridColumn: "1 / -1" }}>
          <span className="pf-label">Address</span>
          <textarea
            className="pf-input pf-textarea"
            rows={3}
            value={form.address}
            onChange={(e) => setField("address", e.target.value)}
            placeholder="Street, City, Postcode"
            autoComplete="street-address"
          />
        </label>

        <div className="pf-actions" style={{ gridColumn: "1 / -1" }}>
          <button type="submit" className="pf-btn pf-btn--primary" disabled={saving || readOnly}>
            {saving ? "Saving…" : readOnly ? "Read-only" : "Save changes"}
          </button>
          <button type="button" className="pf-btn" onClick={handleLogoutClick}>
            Log out
          </button>
          <button type="button" className="pf-btn pf-btn--danger" onClick={() => setShowDel(true)}>
            Delete account
          </button>
        </div>
      </form>

      <section className="pf-card pf-security-card">
        <div className="pf-card-head" style={{ alignItems: "center" }}>
          <div>
            <div className="pf-card-title">Two-factor authentication</div>
            <div className="pf-subtle">
              {mfaState.loading
                ? "Checking your security settings…"
                : mfaState.enabled
                  ? "Enabled — you'll be asked for a code on sign-in."
                  : "Off — turn this on to protect your account."}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {mfaBusy ? <span className="pf-subtle">Working…</span> : null}
            <button
              type="button"
              className={"pf-switch" + (mfaState.enabled ? " is-on" : "")}
              role="switch"
              aria-checked={mfaState.enabled}
              aria-label={mfaState.enabled ? "Disable two-factor authentication" : "Enable two-factor authentication"}
              onClick={handleMfaToggle}
              disabled={mfaState.loading || mfaBusy || isManagerReadOnlyView}
            >
              <span className="pf-switch-knob" />
            </button>
          </div>
        </div>

        {mfaMsg ? <div className="pf-alert pf-alert--ok" style={{ marginTop: 10 }}>{mfaMsg}</div> : null}
        {mfaErr ? <div className="pf-alert pf-alert--err" style={{ marginTop: 10 }}>{mfaErr}</div> : null}

        <div className="pf-security-footer">
          <button type="button" className="pf-btn pf-btn--ghost" onClick={() => navigate("/security")}>
            Open Security
          </button>
          {mfaState.enabled ? (
            <span className="pf-subtle">Tip: disabling MFA requires verification.</span>
          ) : (
            <span className="pf-subtle">You’ll set up an authenticator app and recovery codes there.</span>
          )}
        </div>

        {isManagerReadOnlyView ? (
          <div className="pf-alert" style={{ marginTop: 10 }}>
            Two-factor settings are locked while you’re in account manager read-only mode.
          </div>
        ) : null}
      </section>

      <section className="pf-card">
        <div className="pf-card-head">
          <div className="pf-card-title">Storage usage</div>
          <div className="pf-subtle">
            {formatBytes(usage.storageBytes)} / {formatBytes(limitBytes)} ({pct}%)
          </div>
        </div>

        <div className="pf-progress">
          <div className="pf-progress-bar" style={{ width: `${pct}%` }} />
        </div>

        {storageAtLimit ? (
          <div className="pf-alert pf-alert--err" style={{ marginTop: 10 }}>
            You’ve reached the storage included in this workspace. Contact an administrator if you need more space.
          </div>
        ) : null}

        <div className="pf-progress-footer">
          <span className="pf-subtle">
            {usage.updatedAt
              ? `Updated: ${new Date(
                  usage.updatedAt.seconds ? usage.updatedAt.seconds * 1000 : usage.updatedAt
                ).toLocaleString()}`
              : "Updated periodically"}
          </span>
          <button type="button" className="pf-btn pf-btn--ghost" onClick={fetchUsage} disabled={loadingUsage}>
            {loadingUsage ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </section>

      <section className="pf-card pf-reset-card">
        <div className="pf-reset">
          <div>
            <div className="pf-reset-title">Reset password</div>
            <p className="pf-reset-desc">Send yourself a password reset link.</p>
          </div>
          <button type="button" className="pf-btn pf-btn--ghost" onClick={handleSendResetLink} disabled={resetBusy}>
            {resetBusy ? "Sending…" : "Send reset link"}
          </button>
        </div>

        {resetMsg ? <div className="pf-alert pf-alert--ok pf-reset-alert">{resetMsg}</div> : null}
        {resetErr ? <div className="pf-alert pf-alert--err pf-reset-alert">{resetErr}</div> : null}
      </section>

      {!user?.emailVerified ? (
        <div className="pf-alert" style={{ marginTop: 10 }}>
          Please verify your email address from your inbox before relying on this account for access and notifications.
        </div>
      ) : null}

      <style>{`
        .pf-wrap{max-width:900px;margin:16px auto;padding:0 16px}
        .pf-header{display:flex;align-items:center;justify-content:space-between;margin:8px 0 6px}
        .pf-title{font-size:22px;font-weight:800;color:#111827}
        .pf-statuses{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:8px}
        .pf-badge{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:999px;border:1px solid #e5e7eb;background:#f9fafb;font-size:12px;color:#374151}
        .pf-badge--ok{background:#ecfdf5;border-color:#a7f3d0;color:#065f46}
        .pf-badge--admin{background:#f3e8ff;border-color:#d8b4fe;color:#6b21a8}
        .pf-card{border:1px solid #e5e7eb;border-radius:14px;padding:12px 14px;background:#fff;margin-bottom:14px}
        .pf-card-head{display:flex;align-items:baseline;justify-content:space-between}
        .pf-card-title{font-weight:700;color:#111827}
        .pf-subtle{color:#6b7280;font-size:12px}
        .pf-progress{height:10px;border-radius:999px;background:#f3f4f6;overflow:hidden;margin:10px 0}
        .pf-progress-bar{height:100%;background:linear-gradient(90deg,#4f46e5,#7c3aed)}
        .pf-progress-footer{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap}
        .pf-grid{display:grid;grid-template-columns:1fr;gap:14px}
        @media(min-width:720px){.pf-grid{grid-template-columns:1fr 1fr}}
        .pf-row{display:flex;flex-direction:column;gap:6px}
        .pf-label{color:#6b7280;font-size:13px;font-weight:600}
        .pf-input,.pf-btn{
          -webkit-appearance:none;
          appearance:none;
          box-shadow:none !important;
          text-shadow:none !important;
          background-image:none !important;
          filter:none;
        }
        .pf-input:focus,.pf-btn:focus{
          outline:2px solid transparent;
          outline-offset:2px;
          box-shadow:none !important;
          text-shadow:none !important;
          background-image:none !important;
        }
        .pf-input{height:40px;padding:0 12px;border:1px solid:#d6d6e7;border-radius:10px}
        .pf-input[readonly]{background:#f9fafb;color:#6b7280}
        .pf-textarea{height:auto;padding:10px 12px}
        .pf-actions{display:flex;gap:10px;flex-wrap:wrap;margin-top:8px}
        .pf-btn{height:40px;padding:0 14px;border-radius:10px;border:1px solid:#cfcfe6;background:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:flex-start;text-align:left}
        .pf-btn--primary{background:#4f46e5;color:#fff;border-color:#4f46e5}
        .pf-btn--danger{background:#dc2626;color:#fff;border-color:#dc2626}
        .pf-btn--ghost{background:transparent;border-color:#e5e7eb;color:#374151}
        .pf-btn:disabled{opacity:.6;cursor:not-allowed}
        .pf-alert{padding:10px 12px;border-radius:10px;border:1px solid #e5e7eb;background:#f9fafb}
        .pf-alert--ok{background:#ecfdf5;border-color:#a7f3d0;color:#065f46}
        .pf-alert--err{background:#fee2e2;border-color:#fecaca;color:#7a0b0b}
        .pf-reset-card{margin-top:16px}
        .pf-reset{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;flex-wrap:wrap}
        .pf-reset-title{font-weight:600;color:#111827;font-size:15px}
        .pf-reset-desc{margin:4px 0 0;color:#6b7280;font-size:13px}
        .pf-reset-alert{margin-top:12px}
        .pf-security-footer{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-top:12px}
        .pf-switch{width:54px;height:32px;border-radius:999px;border:1px solid #e5e7eb;background:#f3f4f6;display:inline-flex;align-items:center;padding:3px;cursor:pointer;position:relative;transition:background .18s ease, border-color .18s ease}
        .pf-switch:disabled{opacity:.6;cursor:not-allowed}
        .pf-switch .pf-switch-knob{width:26px;height:26px;border-radius:999px;background:#fff;border:1px solid #e5e7eb;box-shadow:0 8px 16px rgba(15,23,42,.12);transform:translateX(0);transition:transform .18s ease}
        .pf-switch.is-on{background:linear-gradient(90deg,#4f46e5,#7c3aed);border-color:#4f46e5}
        .pf-switch.is-on .pf-switch-knob{transform:translateX(22px);border-color:rgba(255,255,255,.6)}
      `}</style>

      {showDel ? (
        <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000}}>
          <div style={{width:"100%", maxWidth:520, background:"#fff", borderRadius:12, border:"1px solid #e5e7eb", boxShadow:"0 20px 50px rgba(0,0,0,.2)", padding:18}}>
            <h3 style={{ fontSize:18, fontWeight:900, margin:"0 0 8px" }}>Delete account</h3>
            <p style={{ color:"#374151", margin:"0 0 10px" }}>
              This will permanently delete your account and <strong>all associated content</strong> from our systems.
              This action is <strong>irreversible</strong>.
            </p>

            {delErr ? <div className="pf-alert pf-alert--err" style={{ marginBottom: 10 }}>{delErr}</div> : null}

            <div className="pf-actions">
              <button className="pf-btn pf-btn--danger" onClick={handleDeleteAccount} disabled={delBusy}>
                {delBusy ? "Deleting…" : "Yes, delete my account"}
              </button>
              <button className="pf-btn pf-btn--ghost" onClick={() => setShowDel(false)} disabled={delBusy}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
